// Space overview (v0.4): all tasks of all collections of a space, combined, with the same views as
// a collection. The board groups by status category; cards and rows link to their collection.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { errorMessage } from "../api";
import type { CreateItem, Item, SpaceCollectionInfo, Status, StatusCategory } from "../api/types";
import { EmptyState, ErrorBox, Spinner } from "../components/bits";
import { Icon } from "../components/icons";
import { QuickAdd } from "../components/QuickAdd";
import { CATEGORY_LABELS } from "../lib/format";
import {
  categoryColumns,
  filterItems,
  groupItems,
  pickTargetCollection,
  statusForCategory,
} from "../lib/kanban";
import {
  createItem,
  loadSpaceItems,
  moveItem,
  savedSpaceTarget,
  saveSpaceTarget,
  setCurrentSpace,
  toastError,
} from "../store/app";
import { useActors, useApp } from "../store/hooks";
import { Board, type BoardColumnDef } from "./BoardView";
import { CalendarView } from "./CalendarView";
import { VIEW_ICONS, VIEW_NAMES } from "./CollectionPage";
import { CollectionChip, GroupedRows, GroupToggle } from "./ListView";
import { TimelineView } from "./TimelineView";

type SpaceView = "board" | "list" | "calendar" | "timeline";
const VIEWS: SpaceView[] = ["board", "list", "calendar", "timeline"];

export function SpacePage() {
  const { spaceId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const space = useApp((s) => s.spaces?.find((x) => x.id === spaceId));
  const summaries = useApp((s) => s.collections[spaceId]);
  const allItems = useApp((s) => s.items);
  // Refetch when any collection of the space changes (realtime collection.changed).
  const version = useApp((s) =>
    (s.collections[spaceId] ?? []).reduce((n, c) => n + (s.collectionVersion[c.id] ?? 0), 0),
  );
  const actorList = useActors();
  const [info, setInfo] = useState<{ collections: SpaceCollectionInfo[]; statuses: Status[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [includeDone, setIncludeDone] = useState(false);
  const [q, setQ] = useState("");
  const [listGroup, setListGroup] = useState<"collection" | "status">("collection");
  const [target, setTarget] = useState<string | null>(null);

  const view: SpaceView = VIEWS.find((v) => v === params.get("view")) ?? "board";

  const reload = () => {
    setError(null);
    loadSpaceItems(spaceId, includeDone)
      .then((r) => setInfo({ collections: r.collections, statuses: r.statuses }))
      .catch((e) => setError(errorMessage(e)));
  };
  useEffect(() => {
    if (!spaceId) return;
    setCurrentSpace(spaceId);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceId, includeDone, version]);
  useEffect(() => setInfo(null), [spaceId]);

  const collections = useMemo(() => info?.collections ?? [], [info]);
  const collectionIds = useMemo(() => new Set(collections.map((c) => c.id)), [collections]);
  const nameOf = useMemo(() => new Map(collections.map((c) => [c.id, c.name])), [collections]);
  const schemeOf = useMemo(
    () => new Map((summaries ?? []).map((c) => [c.id, c.status_scheme_id])),
    [summaries],
  );
  const statuses = useMemo(() => [...(info?.statuses ?? [])].sort((a, b) => a.position - b.position), [info]);
  const statusesOf = useCallback(
    (collectionId: string) => {
      const scheme = schemeOf.get(collectionId);
      return scheme ? statuses.filter((s) => s.scheme_id === scheme) : statuses;
    },
    [schemeOf, statuses],
  );

  const items = useMemo(
    () =>
      filterItems(
        Object.values(allItems).filter((i) => i.space_id === spaceId && collectionIds.has(i.collection_id)),
        { includeDone, q },
      ),
    [allItems, spaceId, collectionIds, includeDone, q],
  );

  // Quick-add target: last used collection of this space, else the first one.
  useEffect(() => {
    if (info) setTarget(pickTargetCollection(info.collections, savedSpaceTarget(spaceId)));
  }, [info, spaceId]);
  const targetStatuses = target ? statusesOf(target) : [];

  const create = async (body: CreateItem, category?: StatusCategory) => {
    if (!target) return;
    const status = category ? statusForCategory(statuses, schemeOf.get(target), category) : undefined;
    if (category && !status) {
      toastError(new Error(`“${nameOf.get(target)}” has no ${CATEGORY_LABELS[category]} status`));
      return;
    }
    try {
      await createItem(target, status ? { ...body, status_id: body.status_id ?? status.id } : body);
      saveSpaceTarget(spaceId, target);
    } catch (e) {
      toastError(e, "Could not create task");
      throw e;
    }
  };

  const chip = useCallback(
    (item: Item) => <CollectionChip id={item.collection_id} name={nameOf.get(item.collection_id) ?? "…"} />,
    [nameOf],
  );

  const board = useMemo(() => categoryColumns(items), [items]);
  const boardColumns = useMemo<BoardColumnDef[]>(
    () => board.keys.map((cat) => ({ key: cat, title: CATEGORY_LABELS[cat], category: cat })),
    [board.keys],
  );
  const groups = useMemo(
    () => groupItems(items, listGroup, { collections }),
    [items, listGroup, collections],
  );

  if (error && !info) return <div className="page"><ErrorBox error={error} onRetry={reload} /></div>;

  const usable = collections.filter((c) => c.kind !== "notebook");

  return (
    <div className="page space-page">
      <header className="page-head">
        <h1>{space?.name ?? "Space"}</h1>
        <div className="seg" role="tablist" aria-label="View">
          {VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={v === view}
              className={v === view ? "active" : ""}
              onClick={() => setParams({ view: v }, { replace: true })}
            >
              <Icon name={VIEW_ICONS[v]} size={14} />
              <span className="seg-label">{VIEW_NAMES[v]}</span>
            </button>
          ))}
        </div>
      </header>

      <div className="space-toolbar">
        <input
          type="search"
          className="space-search"
          placeholder="Search tasks…"
          aria-label="Search tasks"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <label className="check-row">
          <input type="checkbox" checked={includeDone} onChange={(e) => setIncludeDone(e.target.checked)} />
          <span>Include done</span>
        </label>
        {view === "list" && (
          <GroupToggle
            value={listGroup}
            options={[
              { value: "collection", label: "Collection" },
              { value: "status", label: "Status" },
            ]}
            onChange={setListGroup}
          />
        )}
      </div>

      {usable.length > 0 && (
        <div className="space-add">
          <label className="space-target">
            <span className="muted small">Add to</span>
            <select
              aria-label="Target collection"
              value={target ?? ""}
              onChange={(e) => {
                setTarget(e.target.value);
                saveSpaceTarget(spaceId, e.target.value);
              }}
            >
              {usable.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <QuickAdd
            statuses={targetStatuses}
            actors={actorList}
            defaultStatusId={statusForCategory(targetStatuses, undefined, "todo")?.id}
            onCreate={(body) => create(body)}
          />
        </div>
      )}

      {!info ? (
        <Spinner />
      ) : collections.length === 0 ? (
        <EmptyState title="No collections in this space">
          <p className="muted">Create a collection from the sidebar to add tasks here.</p>
        </EmptyState>
      ) : view === "board" ? (
        <Board
          columns={boardColumns}
          cards={board.columns}
          items={items}
          cardExtra={chip}
          // Move-API neighbours must be siblings in the same collection.
          canBeSibling={(a, b) => a.collection_id === b.collection_id}
          onCreate={target ? (cat, body) => create(body, cat as StatusCategory) : undefined}
          quickAdd={{ statuses: targetStatuses }}
          onMoveCard={(id, cat, m) =>
            void moveItem(id, {
              status_category: cat as StatusCategory,
              after_id: m.afterId,
              before_id: m.beforeId,
              position: m.position,
            }).catch((err) => toastError(err, "Move failed"))
          }
        />
      ) : view === "calendar" ? (
        <CalendarView items={items} />
      ) : view === "timeline" ? (
        <TimelineView items={items} statuses={statuses} />
      ) : (
        <div className="list-view">
          <GroupedRows
            key={listGroup}
            groups={groups}
            statusesOf={(it) => statusesOf(it.collection_id)}
            collectionName={(it) => nameOf.get(it.collection_id)}
          />
          {groups.length === 0 && <p className="muted pad">{q ? "No matching tasks." : "No open tasks."}</p>}
        </div>
      )}
    </div>
  );
}
